import React from 'react';
import { Box, Divider, Group } from '@mantine/core';
import { useNotifications } from '@mantine/notifications';
import { useForm } from '@mantine/hooks';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

import { useIsMobileMedia, useNextPath, useRequestStates } from '~/hooks';
import { authContextSlice, useAuthContext } from '~/pages/LandingPage/contexts/AuthContext';
import { LabelMedium, LabelMediumStrong, LabelSmall, LabelSmallStrong, ParagraphSmall } from '~/pages/LandingPage/typography';
import * as constants from '~/constants';
import { LoaderWithOverlay } from '~/components/molecules';
import { useLoginMutation, useRegisterMutation } from '~/redux/slices/userSlice';
import { getFirebaseAuth } from '~/lib/firebase';

import GoogleIcon from '~/assets/companyLogos/google.svg';

import * as Styles from '../AuthModal.styled';

const NOTIFICATION_TIMEOUT = 5000;

const SignupView = () => {
  const isMobileMedia = useIsMobileMedia();
  const { showNotification } = useNotifications();
  const navigate = useNavigate();
  const nextPath = useNextPath('/candidates/browse/');

  const { dispatchAuthCtx } = useAuthContext();

  const [registerUser] = useRegisterMutation();
  const [loginUser] = useLoginMutation();

  const [signupReqStates, signupReqStateHandler] = useRequestStates();

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    errorMessages: {
      firstName: 'This should not be blank (max 64 characters)',
      lastName: 'This should not be blank (max 64 characters)',
      email: 'Please use your official email address',
      password: '',
    },
    validationRules: {
      firstName: (value) => value !== '' && value.length <= 64,
      lastName: (value) => value.length <= 64,
      email: (value) => {
        const domain = value.split('@')?.[1];
        if (domain) {
          if (!constants.BLACKLISTED_DOMAINS.includes(domain)) {
            return true;
          }
        }
        return false;
      },
    },
  });

  const handleSignupWithGoogle = async () => {
    const auth = getFirebaseAuth();
    const google = new GoogleAuthProvider();

    // Create Firebase Account
    let firebaseUserCreds;
    try {
      signupReqStateHandler.setPending();
      firebaseUserCreds = await signInWithPopup(auth, google);
    } catch (e) {
      let errorMsg = 'Something went wrong';
      if (e.code === 'auth/popup-closed-by-user') {
        errorMsg = 'Looks like you closed the authentication popup. Please try again.';
      }
      showNotification({ title: 'Error', message: errorMsg, autoClose: NOTIFICATION_TIMEOUT, color: 'red' });
      signupReqStateHandler.setRejected();
      return;
    }

    // Create Upraised Account
    const [firstName, lastName] = [...firebaseUserCreds.user.displayName.split(' '), ''];
    const resp = await registerUpraisedAccount(firebaseUserCreds, firstName, lastName);
    if (resp.error) {
      const errorCode = resp.error.data?.errorCode;
      let errorMsg = resp.error.data?.error || 'Failed to register. Please try again';
      if (errorCode === 'USER_ALREADY_REGISTERED') {
        const { errorMsg: loginErrorMsg } = await loginUpraisedAccount(firebaseUserCreds);
        errorMsg = loginErrorMsg;
      }
      if (errorMsg) {
        showNotification({ title: 'Error', message: errorMsg, autoClose: NOTIFICATION_TIMEOUT, color: 'red' });
      }
      signupReqStateHandler.setRejected();
    } else {
      signupReqStateHandler.setFulfilled();
      navigate(nextPath);
    }
  };

  const handleRegisterClick = async ({ email, password, firstName, lastName }) => {
    /**
     * There is a possibility where the Firebase user is created but
     * API request failed while creating the Upraised account.
     * This is handled in the `catch` block
     */

    // Create Firebase account
    let firebaseUserCreds;
    try {
      // Avoid attempting to create duplicate firebase user
      if (!signupReqStates.isFulfilled) {
        signupReqStateHandler.setPending();
        const auth = getFirebaseAuth();
        firebaseUserCreds = await createUserWithEmailAndPassword(auth, email, password);
      } else {
        firebaseUserCreds = signupReqStates.data;
      }
    } catch (e) {
      let errorMsg = `Something went wrong. Code: ${e.code}`;
      if (e.code === 'auth/email-already-in-use') {
        /**
         * // TODO:
         * Check if Upraised account exists
         * If yes, then show an alert to login
         * If no, then create the Upraised hire user and proceed with email verification flow
         * For now, just ask the user to login
         */
        errorMsg = "Looks like you're already registered. Please use login instead.";
      }
      showNotification({ title: 'Error', message: errorMsg, autoClose: NOTIFICATION_TIMEOUT, color: 'red' });
      signupReqStateHandler.setRejected();
      return;
    }

    // Create Upraised account
    const resp = await registerUpraisedAccount(firebaseUserCreds, firstName, lastName);
    if (resp.error) {
      let errorMsg = '';
      errorMsg = resp.error.data?.error || 'Failed to register. Please try again';
      showNotification({ title: 'Error', message: errorMsg, autoClose: NOTIFICATION_TIMEOUT, color: 'red' });
      signupReqStateHandler.setRejected();
    } else {
      signupReqStateHandler.setFulfilled(firebaseUserCreds);
      navigate('/auth', {
        state: {
          sendVerificationEmail: true,
          upraisedUser: resp.data.user,
        },
      });
      // dispatchAuthCtx(authContextSlice.actions.setView('verify-email'));
    }
  };

  const handleLoginClick = () => {
    dispatchAuthCtx(authContextSlice.actions.setView('login'));
  };

  // Helper methods =============
  const registerUpraisedAccount = async (firebaseUserCreds, firstName, lastName) => {
    const idToken = firebaseUserCreds?.user.accessToken;
    const payload = { firstName, lastName, idToken };
    const resp = await registerUser(payload);
    return resp;
  };

  const loginUpraisedAccount = async (firebaseUserCreds) => {
    const upraisedLoginResp = await loginUser({ idToken: firebaseUserCreds.user.accessToken });
    let errorMsg = '';
    if (upraisedLoginResp.error) {
      const data = upraisedLoginResp.error.data;
      const errorCode = data.errorCode;
      if (errorCode === 'INVALID_FIREBASE_TOKEN') {
        errorMsg = 'Failed to authenticate.';
      } else if (errorCode === 'USER_DOES_NOT_EXIST') {
        errorMsg = 'User account not found.';
      }
    }
    return { resp: upraisedLoginResp, errorMsg };
  };

  const isLoading = signupReqStates.isPending;
  return (
    <Box component="form" onSubmit={form.onSubmit(handleRegisterClick)}>
      <LoaderWithOverlay visible={isLoading} />
      <Styles.ContinueWithGoogleBtn
        mt={isMobileMedia ? '24px' : '32px'}
        fullWidth
        leftIcon={<GoogleIcon width="24px" />}
        onClick={handleSignupWithGoogle}
      >
        <LabelMediumStrong>Continue with Google</LabelMediumStrong>
      </Styles.ContinueWithGoogleBtn>
      <Divider
        my={isMobileMedia ? '16px' : '24px'}
        label={<LabelSmall color="#818181">OR</LabelSmall>}
        labelPosition="center"
      />
      <Group grow spacing="20px" direction="row" noWrap align={'flex-start'}>
        <Styles.FormTextInput
          maxLength={64}
          label={<LabelMedium>First Name</LabelMedium>}
          placeholder="Eg. Rohan"
          {...form.getInputProps('firstName')}
        />
        <Styles.FormTextInput
          maxLength={64}
          label={<LabelMedium>Last Name</LabelMedium>}
          placeholder="Eg. Shah"
          {...form.getInputProps('lastName')}
        />
      </Group>
      <Box mt={isMobileMedia ? '16px' : '24px'}>
        <Styles.FormTextInput
          label={<LabelMedium>Work Email</LabelMedium>}
          type="email"
          placeholder="E.g. rohan@company.com"
          {...form.getInputProps('email')}
        />
      </Box>
      <Box mt={isMobileMedia ? '16px' : '24px'}>
        <Styles.FormPasswordInput
          label={<LabelMedium>Password</LabelMedium>}
          type="password"
          placeholder="Create a password"
          {...form.getInputProps('password')}
        />
      </Box>
      <Box mt="32px">
        <Styles.PrimaryBtn fullWidth type="submit">
          <LabelMediumStrong color="#fff">Proceed</LabelMediumStrong>
        </Styles.PrimaryBtn>
      </Box>
      <Group mt="12px" spacing="4px">
        <ParagraphSmall>Already have an account?</ParagraphSmall>
        <LabelSmallStrong color="#1F7AE0" onClick={handleLoginClick} style={{ cursor: 'pointer' }}>
          Log In
        </LabelSmallStrong>
      </Group>
    </Box>
  );
};

export default SignupView;
